import config from "@/v2-backoffice-client/config"
import type { InvitationFragment } from "@/v2-backoffice-client/invitation/__types__/invitation.fragment"
import { MINUTE_LIMIT_OPTIONS } from "@/v2-backoffice-client/invitation/invitation.constants"

const MINUTES_PER_DAY = 1440
// Pasted minute-limit values up to this are interpreted as days (matches
// MINUTE_LIMIT_OPTIONS, which top out at 30 days). Anything larger is taken
// to be raw minutes so users can paste exact values too.
const MINUTE_LIMIT_DAYS_THRESHOLD = 60

// Mirrors apps/backends/v2-backoffice-api/src/invitation/invitation.api.go
// `SendInvitationEmail`. The copied URL must match the URL the
// invitation email links to, so the recipient gets the same redeem
// flow whether they click the email or paste a link from an admin.
// `consoleAppUrl` is resolved in @/v2-backoffice-client/config to
// follow the same priority chain as the backend's sendgrid.AppBaseUrl.
export function getSignupUrl(inv: InvitationFragment) {
  const base = `${config.consoleAppUrl}/redeem/${encodeURIComponent(inv.Email)}/${inv.Referral}`
  const q = new URLSearchParams()
  if (inv.FirstName) q.set("givenName", inv.FirstName)
  if (inv.LastName) q.set("surname", inv.LastName)
  const qs = q.toString()
  return qs ? `${base}?${qs}` : base
}

// Resolves a pasted role cell against the loaded `UserTenantType` list.
// Accepts either a numeric code or a case-insensitive type name.
export function parseUserTenantTypeCode(
  text: string,
  types: ReadonlyArray<{ Code: number; Name: string }>,
): number | undefined {
  const trimmed = text.trim()
  if (trimmed.length === 0) return undefined

  const asNumber = Number(trimmed)
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber

  const lower = trimmed.toLowerCase()
  const match = types.find((type) => type.Name.toLowerCase() === lower)
  return match?.Code
}

// Resolves a pasted minute-limit cell. Accepts an option label ("14 days"),
// a "Nd"/"N day(s)" form, a small number (treated as days), or a raw minute
// count.
export function parseMinuteLimit(text: string): number | undefined {
  const trimmed = text.trim()
  if (trimmed.length === 0) return undefined

  const lower = trimmed.toLowerCase()
  const optionByLabel = MINUTE_LIMIT_OPTIONS.find((opt) => {
    return opt.label.toLowerCase() === lower
  })
  if (optionByLabel) return optionByLabel.value

  const daysMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:d|day|days)$/i)
  if (daysMatch) {
    return Math.round(Number(daysMatch[1]) * MINUTES_PER_DAY)
  }

  const asNumber = Number(trimmed)
  if (Number.isFinite(asNumber) && asNumber > 0) {
    if (asNumber <= MINUTE_LIMIT_DAYS_THRESHOLD) {
      return Math.round(asNumber * MINUTES_PER_DAY)
    }
    return Math.round(asNumber)
  }

  return undefined
}

// The invite forms' typing-time note for addresses that already belong to
// a Backoffice user. Deliberately NOT "they can sign in as they are": a
// v1-era user who hasn't activated Fusion login cannot, and an invitation
// dead-ends for them — Send runs the full check (UserMigratePromptDialog)
// and offers the activation instead. Empty for none.
export function describeExistingInvitees(count: number) {
  if (count === 0) return ""
  if (count === 1) {
    return "1 invitee is already a Placepoint user — if they haven't activated Fusion login yet, Send offers an activation instead of the invitation."
  }
  return `${count} invitees are already Placepoint users — those without Fusion login get offered an activation instead when you send.`
}

export function copyToClipboard(text: string) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
  }
  const textarea = document.createElement("textarea")
  textarea.value = text
  textarea.style.position = "fixed"
  textarea.style.opacity = "0"
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand("copy")
  textarea.remove()
  return Promise.resolve()
}
